import React, { useState } from "react";
import styled from "styled-components";
import { Input, Button, message as antdMessage } from "antd";
import {
  MailOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { device } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";

const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    // Reset error state
    setError(null);

    // Validate email
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setIsLoading(true);

      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      const response = await fetch(`${apiUrl}/marketing/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          source: "homepage",
        }),
      });

      const result = await response.json();

      if (response.ok || result.success) {
        setIsSuccess(true);
        setEmail("");
        setFirstName("");

        // Track successful subscription
        trackEvent("newsletter_signup", {
          source: "homepage",
          email: email.trim(),
        });

        // Show success message
        antdMessage.success("Thanks! We'll keep you updated.");
      } else {
        // Handle API error
        setError(
          result.error ||
            result.message ||
            "Subscription failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      setError("Unable to connect. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  if (isSuccess) {
    return (
      <NewsletterContainer id="stay-in-the-loop">
        <NewsletterContent>
          <SuccessState>
            <SuccessIcon>
              <CheckCircleOutlined />
            </SuccessIcon>
            <SuccessTitle>You&apos;re all set!</SuccessTitle>
            <SuccessMessage>
              We&apos;ll send updates on new features and what Clumse AI can do. You
              can unsubscribe anytime.
            </SuccessMessage>
          </SuccessState>
        </NewsletterContent>
      </NewsletterContainer>
    );
  }

  return (
    <NewsletterContainer id="stay-in-the-loop">
      <NewsletterContent>
        <NewsletterHeader>
          <NewsletterIcon>
            <MailOutlined />
          </NewsletterIcon>
          <NewsletterTitle>Stay in the Loop</NewsletterTitle>
          <NewsletterSubtitle>
            Get updates on new Clumse features, product improvements, and
            see what Clumse AI can do. Perfect if you're not quite ready to sign up
            yet.
          </NewsletterSubtitle>
        </NewsletterHeader>

        <SignupForm onSubmit={handleSubscribe}>
          <FormRow>
            <FormGroup $half>
              <StyledInput
                type="text"
                placeholder="First name (optional)"
                value={firstName}
                onChange={handleFirstNameChange}
                disabled={isLoading}
                size="large"
              />
            </FormGroup>
            <FormGroup $half>
              <StyledInput
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
                required
                size="large"
                status={error ? "error" : ""}
              />
            </FormGroup>
          </FormRow>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton
            type="primary"
            htmlType="submit"
            size="large"
            disabled={isLoading || !email.trim()}
            icon={
              isLoading ? (
                <LoadingOutlined style={{ color: "#ffffff" }} />
              ) : (
                <MailOutlined style={{ color: "#ffffff" }} />
              )
            }
          >
            {isLoading ? "Subscribing..." : "Subscribe to Updates"}
          </SubmitButton>

          <PrivacyNote>
            We respect your privacy. Unsubscribe anytime. No spam, just updates
            on Clumse.
          </PrivacyNote>
        </SignupForm>
      </NewsletterContent>
    </NewsletterContainer>
  );
};

const IND_ORANGE = "#FF5722";
const SLATE_800 = "#1e293b";
const SLATE_700 = "#334155";
const SLATE_200 = "#e2e8f0";
const WHITE = "#ffffff";

const NewsletterContainer = styled.section`
  padding: 4rem 2rem 4.5rem;
  background: ${WHITE};
  border-top: 2px solid ${SLATE_800};
  margin: 0;
  max-width: 100%;
  position: relative;
  box-sizing: border-box;
  width: 100%;

  @media ${device.mobile} {
    padding: 2.75rem 1rem 3rem;
  }
`;

const NewsletterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const NewsletterHeader = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;

  @media ${device.mobile} {
    margin-bottom: 1rem;
  }
`;

const NewsletterIcon = styled.div`
  font-size: 2.25rem;
  color: ${IND_ORANGE};
  margin-bottom: 0.65rem;

  .anticon {
    font-size: 2.25rem;
  }

  @media ${device.mobile} {
    font-size: 2rem;
    margin-bottom: 0.5rem;

    .anticon {
      font-size: 2rem;
    }
  }
`;

const NewsletterTitle = styled.h2`
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(1.35rem, 3.2vw, 1.85rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${SLATE_800};
  margin: 0 0 1rem;
  text-align: center;
  line-height: 1.2;

  @media ${device.mobile} {
    margin-bottom: 0.75rem;
    letter-spacing: 0.05em;
  }
`;

const NewsletterSubtitle = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  color: ${SLATE_700};
  font-family: "Inter", system-ui, sans-serif;
  max-width: 36rem;
  margin: 0 auto;
  font-weight: 400;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const SignupForm = styled.form`
  background: ${WHITE};
  border-radius: 0;
  padding: 2rem 2rem 2.25rem;
  box-shadow: none;
  border: 2px solid ${SLATE_800};
  max-width: 800px;
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 1.5rem 1.15rem 1.65rem;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  flex: ${(props) => (props.$half ? "1" : "auto")};
  width: ${(props) => (props.$half ? "auto" : "100%")};

  @media ${device.mobile} {
    width: 100%;
  }
`;

const StyledInput = styled(Input)`
  font-family: "Inter", system-ui, sans-serif;
  font-size: 1rem;

  &.ant-input-lg {
    padding: 0.75rem 1rem;
    border-radius: 0;
    border: 2px solid ${SLATE_200};
    color: ${SLATE_800};

    &::placeholder {
      color: #64748b;
    }

    &:hover {
      border-color: ${SLATE_700};
    }

    &:focus,
    &.ant-input-focused {
      border-color: ${IND_ORANGE};
      box-shadow: 0 0 0 1px ${IND_ORANGE};
    }
  }

  @media ${device.mobile} {
    &.ant-input-lg {
      font-size: 0.95rem;
      padding: 0.65rem 0.875rem;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #b91c1c;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.85rem;
  background: #fef2f2;
  border-radius: 0;
  border: 2px solid ${SLATE_800};

  @media ${device.mobile} {
    font-size: 0.85rem;
    padding: 0.625rem 0.75rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: auto;
  padding: 0.875rem 1.5rem !important;
  font-size: 1rem !important;
  font-weight: 700 !important;
  font-family: "Inter", system-ui, sans-serif !important;
  border-radius: 0 !important;
  background: ${IND_ORANGE} !important;
  color: ${WHITE} !important;
  border: 2px solid ${SLATE_800} !important;
  box-shadow: none !important;
  letter-spacing: 0.01em;
  transition: background 0.15s ease, transform 100ms steps(2, end) !important;

  &:hover:not(:disabled) {
    background: #f44e1b !important;
    color: ${WHITE} !important;
    border-color: ${SLATE_800} !important;
  }

  &:active:not(:disabled) {
    background: #e84615 !important;
  }

  &:disabled {
    background: #cbd5e1 !important;
    color: ${SLATE_700} !important;
    border-color: ${SLATE_200} !important;
    cursor: not-allowed;
  }

  .anticon {
    font-size: 1.05rem !important;
    color: #ffffff !important;
  }

  @media ${device.mobile} {
    font-size: 0.95rem !important;
    padding: 0.8rem 1.2rem !important;
  }
`;

const PrivacyNote = styled.p`
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.875rem;
  color: ${SLATE_700};
  text-align: center;
  margin: 1.25rem 0 0 0;
  line-height: 1.5;

  @media ${device.mobile} {
    font-size: 0.8rem;
    margin-top: 1rem;
  }
`;

const SuccessState = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  background: ${WHITE};
  border-radius: 0;
  box-shadow: none;
  border: 2px solid ${SLATE_800};
  max-width: 800px;
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 1.5rem 1.1rem;
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: ${IND_ORANGE};
  margin-bottom: 1.5rem;

  .anticon {
    font-size: 4rem;
  }

  @media ${device.mobile} {
    font-size: 3rem;
    margin-bottom: 1rem;

    .anticon {
      font-size: 3rem;
    }
  }
`;

const SuccessTitle = styled.h3`
  font-size: clamp(1.35rem, 3vw, 1.75rem);
  font-weight: 800;
  color: ${SLATE_800};
  margin-bottom: 1rem;
  font-family: "Inter", system-ui, sans-serif;
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    margin-bottom: 0.75rem;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  color: ${SLATE_700};
  font-family: "Inter", system-ui, sans-serif;
  max-width: 36rem;
  margin: 0 auto;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

export default NewsletterSignup;
